import { useMemo } from 'react';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import type { SalesCalendarDay } from './types';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const brlCompact = (v: number) => {
    if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
};

type Props = {
    month: Date;
    days: SalesCalendarDay[];
    maxAmount: number;
    onMonthChange: (d: Date) => void;
    onDayClick: (day: SalesCalendarDay | null, date: string) => void;
    selectedDate?: string | null;
};

function pad(n: number) {
    return String(n).padStart(2, '0');
}

function toYmd(d: Date) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function intensityClass(amount: number, max: number) {
    if (max <= 0 || amount <= 0) return '';
    const ratio = amount / max;
    if (ratio >= 0.75) return 'bg-emerald-200/80 hover:bg-emerald-300/90 dark:bg-emerald-900/50 dark:hover:bg-emerald-900/70';
    if (ratio >= 0.4) return 'bg-emerald-100/80 hover:bg-emerald-200/90 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50';
    return 'bg-emerald-50/90 hover:bg-emerald-100 dark:bg-emerald-950/25 dark:hover:bg-emerald-950/40';
}

export default function SalesMonthCalendar({ month, days, maxAmount, onMonthChange, onDayClick, selectedDate }: Props) {
    const { cells, monthLabel } = useMemo(() => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const first = new Date(y, m, 1);
        const startPad = first.getDay();
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const total = Math.ceil((startPad + daysInMonth) / 7) * 7;

        const byDate = days.reduce<Record<string, SalesCalendarDay>>((acc, day) => {
            acc[day.date] = day;
            return acc;
        }, {});

        const cells = [];
        for (let i = 0; i < total; i++) {
            const dayNum = i - startPad + 1;
            const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const date = inMonth ? new Date(y, m, dayNum) : null;
            const dateKey = date ? toYmd(date) : null;
            cells.push({
                key: dateKey ?? `pad-${i}`,
                inMonth,
                dayNum: inMonth ? dayNum : null,
                dateKey,
                day: dateKey ? (byDate[dateKey] ?? null) : null,
                isToday: dateKey ? dateKey === toYmd(new Date()) : false,
            });
        }

        const monthLabel = first.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

        return { cells, monthLabel };
    }, [month, days]);

    const prev = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1));
    const next = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1));

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700/60">
                <button
                    type="button"
                    onClick={prev}
                    className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    aria-label="Mês anterior"
                >
                    <PiCaretLeftBold className="size-4" />
                </button>
                <h2 className="text-base font-semibold capitalize text-gray-900 dark:text-gray-100">{monthLabel}</h2>
                <button
                    type="button"
                    onClick={next}
                    className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    aria-label="Próximo mês"
                >
                    <PiCaretRightBold className="size-4" />
                </button>
            </div>

            <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/80 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700/50 dark:bg-gray-800/40 dark:text-gray-400">
                {WEEKDAYS.map((d) => (
                    <div key={d} className="py-2">{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7">
                {cells.map((cell) => {
                    const hasSales = !!cell.day && cell.day.deals_count > 0;
                    const isSelected = cell.dateKey && cell.dateKey === selectedDate;

                    return (
                        <button
                            key={cell.key}
                            type="button"
                            disabled={!cell.inMonth}
                            onClick={() => cell.dateKey && onDayClick(cell.day, cell.dateKey)}
                            className={cn(
                                'min-h-[6.5rem] border-b border-r border-gray-100 p-2 text-left transition last:border-r-0 dark:border-gray-700/40',
                                !cell.inMonth && 'cursor-default bg-gray-50/60 dark:bg-gray-800/30',
                                cell.inMonth && 'cursor-pointer',
                                cell.isToday && !hasSales && 'bg-amber-50/40 dark:bg-amber-950/20',
                                hasSales && intensityClass(cell.day!.total_amount, maxAmount),
                                isSelected && 'ring-2 ring-inset ring-orange-500',
                            )}
                        >
                            {cell.dayNum != null && (
                                <span
                                    className={cn(
                                        'mb-1 inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold',
                                        cell.isToday ? 'bg-amber-500 text-white' : 'text-gray-700 dark:text-gray-300',
                                    )}
                                >
                                    {cell.dayNum}
                                </span>
                            )}
                            {hasSales && cell.day && (
                                <div className="space-y-0.5">
                                    <p className="text-xs font-bold text-emerald-800 dark:text-emerald-200">
                                        {brlCompact(cell.day.total_amount)}
                                    </p>
                                    <p className="text-[10px] text-emerald-700/80 dark:text-emerald-300/80">
                                        {cell.day.deals_count} {cell.day.deals_count === 1 ? 'venda' : 'vendas'}
                                    </p>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
